// buildMenschenItems.ts — übersetzt eine VerantwortlichkeitValue (+ Gegen-Kontakt)
// in die Save-Items, die der Backend-Endpoint erwartet (Form von PersonCleanupInput).
// Geteilt zwischen „Ansprechpartner bereinigen" (saveBatch) und der Firmen-Anlage /
// den ContactDetails (POST ansprechpartner-cleanup/attach-menschen).

import type { VerantwortlichkeitValue } from "./VerantwortlichkeitInput";
import type { ContactRef } from "../relationsInline/types";

/** Teilmenge von PersonCleanupInput (Backend) — nur die hier gesendeten Felder. */
export type MenschItem = {
  relationId: number;
  personId: number;
  companyId: number;
  isMitarbeiterBav: boolean;
  /**
   * Der PO-Haken „Mensch im Unternehmen (bAV/bKV)" an der VERBINDUNG
   * (ContactRelation.istMenschImUnternehmen). `undefined` heißt „der Aufrufer
   * kennt den Haken nicht" — das Backend lässt ihn dann unangetastet.
   *
   * ⚠️ Nicht zu verwechseln mit dem Backend-Feld `isMenschImUnternehmen`
   * (ohne „t"): das setzt den KONTAKT-Status, nicht das Feld an der Verbindung.
   */
  menschImUnternehmen?: boolean;
  /** Anwender hat den Haken gegen die Ableitung gesetzt → Automatik aus. */
  menschImUnternehmenManuell?: boolean;
  /**
   * „Person ist ausgeschieden?" mit Beginn/Ende (User 28.09.2026). Gilt für das
   * Paar — das Backend schreibt es an alle verwalteten Rollenzeilen. Fehlt das
   * Feld, bleibt der Bestand unangetastet.
   */
  ausgeschieden?: boolean;
  beginn?: string | null;
  ende?: string | null;
  isNew?: boolean;
  role?: "gf" | "gesellschafter" | "mensch";
  isExtern?: boolean;
  verantwortlichkeit?: string;
  externalCompanyId?: number;
  externalCompanyNew?: {
    name: string;
    phone?: string;
    email?: string;
    web?: string;
    branche?: string;
    legalFormId?: number;
  };
  share?: string;
  istGeschaeftsfuehrer?: boolean;
  /** Stabiler Schlüssel je Eintrag: Mehrfach-Rollen derselben (frei eingetippten) Person teilen EINEN neuen Kontakt. */
  newPersonKey?: string;
  /**
   * Die private Assistenz dieser Person (PO 21.09.2026) — höchstens eine, und
   * nur am ERSTEN Item der Person: Sie hängt am Menschen, nicht an einer Rolle.
   * Säße sie an jedem Item, verarbeitete das Backend sie bei einer Person mit
   * drei Rollen dreimal.
   */
  assistenz?: {
    contactId?: number;
    salutationId?: number;
    titleId?: number;
    gender?: string;
    firstName?: string;
    lastName?: string;
    phonePrivate?: string;
    phoneWork?: string;
    mobilePrivate?: string;
    mobileWork?: string;
    emailPrivate?: string;
    emailWork?: string;
  };
  /** Die bestehende Zuordnung der Assistenz lösen (nur die Verknüpfung). */
  assistenzLoesen?: boolean;
  newPerson?: {
    contactId?: number;
    /** true → Gegen-Kontakt ist selbst eine Firma (Gesellschafter-Firma). */
    isFirma?: boolean;
    lastName?: string;
    firstName?: string;
    gender?: string;
    salutationId?: number;
    salutationLabel?: string;
    titleId?: number;
    birthDate?: string;
    phonePrivate?: string;
    phoneWork?: string;
    mobilePrivate?: string;
    mobileWork?: string;
    emailPrivate?: string;
    emailWork?: string;
    /** nur bei isFirma: Firmen-Stammdaten. */
    branche?: string;
    legalFormId?: number;
    web?: string;
  };
};

/**
 * Übersetzt die Rollen EINER VerantwortlichkeitValue in Save-Items. Erste Rolle
 * aktualisiert die bestehende `relationId`; jede weitere Rolle (isNew) legt eine
 * zusätzliche Beziehung an. Bei extern: eine zugeordnete Firma (bestehend ODER neu).
 *
 * Ohne Rolle, aber MIT Haken entsteht genau ein Item ohne `role`: der Mitarbeiter
 * ohne jede Verantwortlichkeit ist der Zweck des Hakens (PO 15.08.2026) — das
 * Backend legt dafür die Trägerbeziehung an.
 */
export function verantToItems(
  relationId: number,
  personId: number,
  companyId: number,
  v: VerantwortlichkeitValue | null,
): MenschItem[] {
  if (!v) return [];
  const haken = !!v.istMenschImUnternehmen;
  const hakenManuell = !!v.istMenschImUnternehmenManuell;
  if (!v.roles?.length) {
    if (!haken) return [];
    return [
      {
        relationId,
        personId,
        companyId,
        isMitarbeiterBav: false,
        menschImUnternehmen: true,
        menschImUnternehmenManuell: hakenManuell,
        ...(v.assistenzLoesen ? { assistenzLoesen: true } : {}),
      },
    ];
  }
  // Die Assistenz hängt an der PERSON, nicht an einer Rolle — sie fährt
  // deshalb nur am ersten Item mit (siehe `MenschItem.assistenz`).
  const a = v.assistenz;
  const assistenz =
    a && (a.contactId || a.lastName?.trim())
      ? {
          contactId: a.contactId,
          salutationId: a.salutationId,
          titleId: a.titleId,
          gender: a.gender,
          firstName: a.firstName,
          lastName: a.lastName?.trim(),
          phonePrivate: a.phonePrivate,
          phoneWork: a.phoneWork,
          mobilePrivate: a.mobilePrivate,
          mobileWork: a.mobileWork,
          emailPrivate: a.emailPrivate,
          emailWork: a.emailWork,
        }
      : undefined;
  const assistenzLoesen = v.assistenzLoesen || undefined;

  const ext = v.isExtern ? v.externalCompany : null;
  const externalCompanyId = ext?.contactId;
  const externalCompanyNew =
    !externalCompanyId && ext?.lastName?.trim()
      ? {
          name: ext.lastName.trim(),
          phone: ext.phone,
          email: ext.email,
          web: ext.web,
          branche: ext.branche,
          legalFormId: ext.legalFormId,
        }
      : undefined;

  return v.roles.map((r, idx): MenschItem => {
    const base = {
      relationId,
      personId,
      companyId,
      isMitarbeiterBav: false,
      isNew: idx > 0,
      // Der Haken hängt an der Verbindung, nicht an der Rolle — jedes Item des
      // Paares trägt denselben Wert, das Backend schreibt ihn einmal je Paar.
      menschImUnternehmen: haken,
      menschImUnternehmenManuell: hakenManuell,
      ...(idx === 0 ? { assistenz, assistenzLoesen } : {}),
    };
    if (r.kind === "gf") return { ...base, role: "gf" };
    if (r.kind === "gesellschafter")
      return { ...base, role: "gesellschafter", share: r.share, istGeschaeftsfuehrer: r.istGeschaeftsfuehrer };
    return {
      ...base,
      role: "mensch",
      isExtern: v.isExtern,
      verantwortlichkeit: r.verantwortlichkeit,
      // Die Quote hängt seit 08/2026 an JEDER Rolle, die laut Matrix eine trägt:
      // Kommanditist (§ 171 HGB, Euro), Wohnungs-/Miteigentümer (Bruchteil),
      // Aktionär (Prozent). Vorher gab es sie nur an `gesellschafter`.
      //
      // ⚠️ Wer hier etwas ändert, muss die LESEPFADE mitziehen — sonst schreibt
      // das Popup beim nächsten Speichern eine leere `share` zurück
      // (attach-menschen ersetzt alle verwalteten Zeilen des Paars):
      //   ContactConnections.relsToVerant · utils/contactToRelationItems.toVerant
      //   AnsprechpartnerCleanupPage (formFromRelation UND die eigene Item-Kopie)
      //   HaushalteCleanupPage.buildFirmaRelations
      share: r.share,
      externalCompanyId: v.isExtern ? externalCompanyId : undefined,
      externalCompanyNew: v.isExtern ? externalCompanyNew : undefined,
    };
  });
}

/**
 * Für Firmen-Anlage / ContactDetails: pro Eintrag (Person-Ref + Verantwortlichkeit)
 * die Save-Items bauen. Jeder Eintrag ist „neu" (isNew) und liefert `newPerson`
 * (bestehender Kontakt via contactId ODER frei eingetippt) — der Server löst/legt an.
 */
export function buildMenschenItems(
  entries: Array<{
    ref: ContactRef | null;
    verant: VerantwortlichkeitValue | null;
    ausgeschieden?: boolean;
    beginn?: string;
    ende?: string;
  }>,
  companyId: number,
): MenschItem[] {
  return entries.flatMap((a, entryIdx) => {
    const ref = a.ref;
    // Nur der Haken, ganz ohne Rolle, ist ein gültiger Eintrag — genau dafür
    // gibt es ihn. Vor 08/2026 fiel so ein Eintrag hier stillschweigend weg.
    if (!ref || (!a.verant?.roles?.length && !a.verant?.istMenschImUnternehmen)) return [];
    const hasRef = !!(ref.contactId || ref.lastName?.trim());
    if (!hasRef) return [];
    const isFirma = ref.type === "Firma";
    const newPerson = ref.contactId
      ? { contactId: ref.contactId, isFirma }
      : isFirma
        ? {
            // Gegen-Kontakt ist selbst eine Firma (Gesellschafter-Firma) → Firmen-
            // Stammdaten; Tel/Mobil/Mail werden serverseitig als Comm angelegt
            // (phoneWork→phone, mobileWork→mobile, emailWork→email, web→homepage).
            isFirma: true,
            lastName: ref.lastName?.trim(),
            branche: ref.branche?.trim() || undefined,
            legalFormId: ref.legalFormId,
            phoneWork: ref.phone?.trim() || undefined,
            mobileWork: ref.mobile?.trim() || undefined,
            emailWork: ref.email?.trim() || undefined,
            web: ref.web?.trim() || undefined,
          }
        : {
            lastName: ref.lastName?.trim(),
            firstName: ref.firstName?.trim(),
            gender: ref.gender,
            salutationId: ref.salutationId,
            salutationLabel: ref.salutationLabel,
            titleId: ref.titleId,
            birthDate: ref.birthDate,
            phonePrivate: ref.phonePrivate?.trim() || undefined,
            phoneWork: ref.phoneWork?.trim() || undefined,
            mobilePrivate: ref.mobilePrivate?.trim() || undefined,
            mobileWork: ref.mobileWork?.trim() || undefined,
            emailPrivate: ref.emailPrivate?.trim() || undefined,
            emailWork: ref.emailWork?.trim() || undefined,
          };
    // Nur mitschicken, wenn der Eintrag das Feld kennt (Bearbeiten-Modus oder
    // ein Eintrittsdatum beim Anlegen) — sonst bleibt der Bestand stehen. Das
    // Eintrittsdatum (`beginn`) gilt seit 28.09.2026 unabhängig vom
    // Ausscheiden; nur das Austrittsdatum (`ende`) hängt daran.
    const beginn = a.beginn?.trim() || null;
    const ausgeschieden =
      typeof a.ausgeschieden === "boolean" || beginn
        ? {
            ausgeschieden: !!a.ausgeschieden,
            beginn,
            ende: a.ausgeschieden ? a.ende?.trim() || null : null,
          }
        : {};
    return verantToItems(0, ref.contactId ?? 0, companyId, a.verant).map((it) => ({
      ...it,
      isNew: true,
      newPerson,
      newPersonKey: `e${entryIdx}`,
      ...ausgeschieden,
    }));
  });
}
